import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { RootState } from "../Store/store";
import { useSiteConfig } from "./useSiteConfig";

/**
 * Free vs premium, for the property page's UI gates (October 2026, Task 10).
 *
 * `isPremium` is the only thing a gate should read. It is true when:
 *   - gating is OFF (SiteConfig.premiumGatingEnabled — off all of October, so
 *     Asher's LA testers get everything free), or
 *   - the signed-in account's tier is "premium".
 *
 * This is display logic only. The server doesn't enforce tiers yet (that's
 * November), and the tier comes from the persisted login payload.
 *
 * QA preview: `?previewTier=free` on any URL shows the free experience even
 * while gating is off, so the locked states can be checked in a real browser.
 * It sticks for the browser tab (sessionStorage); `?previewTier=off` ends it.
 * It can only ever make the viewer's own UI MORE locked.
 */

export type Tier = "free" | "premium";

export interface Entitlements {
  tier: Tier;
  isPremium: boolean;
  gatingEnabled: boolean;
  previewingFree: boolean;
  freePhotoLimit: number;
  ownerPhotoCap: number;
}

const PREVIEW_KEY = "sh_preview_tier";

const readPreview = (): boolean => {
  try {
    return window.sessionStorage.getItem(PREVIEW_KEY) === "free";
  } catch {
    return false;
  }
};

const writePreview = (on: boolean) => {
  try {
    if (on) window.sessionStorage.setItem(PREVIEW_KEY, "free");
    else window.sessionStorage.removeItem(PREVIEW_KEY);
  } catch {
    /* storage blocked — the URL param still works for this render */
  }
};

export const exitFreePreview = () => {
  writePreview(false);
  window.dispatchEvent(new Event(PREVIEW_KEY));
};

export const useEntitlements = (): Entitlements => {
  const { config } = useSiteConfig();
  const user = useSelector((s: RootState) => s?.auth?.user) as any;
  const [searchParams] = useSearchParams();
  const param = searchParams.get("previewTier");
  const [previewingFree, setPreviewingFree] = useState<boolean>(
    param === "free" || (param !== "off" && readPreview())
  );

  useEffect(() => {
    if (param === "free" || param === "off") {
      writePreview(param === "free");
      setPreviewingFree(param === "free");
    }
  }, [param]);

  useEffect(() => {
    const sync = () => setPreviewingFree(readPreview());
    window.addEventListener(PREVIEW_KEY, sync);
    return () => window.removeEventListener(PREVIEW_KEY, sync);
  }, []);

  const tier: Tier = user?.tier === "premium" ? "premium" : "free";
  const gatingEnabled = config.premiumGatingEnabled || previewingFree;
  const isPremium = previewingFree ? false : !gatingEnabled || tier === "premium";

  return {
    tier,
    isPremium,
    gatingEnabled,
    previewingFree,
    freePhotoLimit: config.freeOwnerPhotoLimit,
    ownerPhotoCap: config.ownerPhotoCap,
  };
};
